import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';

import type { Optional } from 'chaika-common-types';

import { SimpleList } from '@store/models/SimpleList';
import { DateHelper, DateType } from '@utils/DateHelper';

import { LegalEntityBranchDataWithEndData, LegalEntityBranchStatus } from './types';

export interface FilterParams {
    legalEntityId?: string;
    organizationId?: string | string[];
    actualOn?: Date;
    statuses?: LegalEntityBranchStatus[];
}

export class LegalEntityBranchList extends SimpleList<LegalEntityBranchDataWithEndData, FilterParams> {
    public static Name = 'legalEntityBranchList' as const;

    public getActualOnDayLegalEntityId(organizationId: Optional<string>, actualOn: DateType): Optional<string> {
        const branches = orderBy(this.getFilteredValues({ organizationId }), 'start', 'desc');

        return branches.find(({ start }) => DateHelper.isSameOrAfter(actualOn, start))?.legalEntityId;
    }

    public getLegalEntityIdsForOrganizations(organizationIds: string[]): string[] {
        const branches = this.getFilteredValues({ organizationId: organizationIds });

        return uniq(branches.map(({ legalEntityId }) => legalEntityId));
    }

    public getLegalEntitiesOrganizationIds(legalEntityIds: string[]): string[] {
        return uniq(flatten(legalEntityIds.map(id => this.getLegalEntityOrganizationIds(id))));
    }

    public getLegalEntityOrganizationIds(legalEntityId: string): string[] {
        return this.values
            .filter(
                branch => branch.legalEntityId === legalEntityId && branch.status === LegalEntityBranchStatus.Active,
            )
            .map(({ organizationId }) => organizationId);
    }

    protected filterValue(
        value: LegalEntityBranchDataWithEndData,
        { legalEntityId, organizationId, statuses }: FilterParams,
    ): boolean {
        return (
            this.filterFieldValueByArray(value, statuses, 'status') &&
            this.filterFieldValue(value, legalEntityId, 'legalEntityId') &&
            (Array.isArray(organizationId)
                ? this.filterFieldValueByArray(value, organizationId, 'organizationId')
                : this.filterFieldValue(value, organizationId, 'organizationId'))
        );
    }
}

export const legalEntityBranchList = new LegalEntityBranchList();
